import React, { useState, useEffect, useMemo } from 'react';
import { employeeApi, projectApi, companyApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { getExplicitLimit } from '@/lib/payrollCalc';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, GitBranch, FolderOpen, Users, ChevronDown, ChevronRight, Building2 } from 'lucide-react';

const AbdLogo = ({ className = 'h-8 w-8' }) => (
  <svg
    aria-label="ABD logo"
    fill="none"
    viewBox="18 8 48 42"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      clipRule="evenodd"
      d="M22.533 38.094h24.408c.313 0 .04-.234-.117-.586l-1.835-3.4a15.982 15.982 0 0 1-.742-1.406H32.375v-.117l3.436-6.251c.703-1.212 1.68-3.165 2.343-4.298l2.343-4.298c.196-.351 1.016-1.992 1.211-2.149.04.196.117.313.235.47l.742 1.328c.859 1.445 1.718 3.047 2.577 4.454 1.015 1.719 2.07 3.672 3.085 5.352l2.07 3.595c.156.273.312.586.508.898.351.586.664 1.172 1.054 1.798.352.586.664 1.172 1.015 1.797l4.14 7.189c.117.234.976 1.602 1.015 1.797H18.667v5.353h46.121c.118 0 .157-.04.157-.156v-4.845c0-.196-.47-.938-.586-1.133l-3.163-5.51c-.118-.195-.196-.35-.313-.546L54.83 26.92c-.117-.196-.195-.352-.313-.547l-6.053-10.471c-.117-.196-.195-.352-.312-.547L45.3 10.393c-.156-.235-.586-.938-.625-1.172h-5.819c-.039.195-.351.664-.468.898-.196.313-.313.586-.508.899l-1.015 1.797c-.196.313-.313.586-.508.899a6.875 6.875 0 0 1-.547.899l-2.07 3.633c-.351.625-.663 1.211-1.015 1.797l-6.248 10.862c-.352.625-.664 1.211-1.016 1.797l-2.616 4.532c-.274.508-.586.82-.274.82"
      fill="#B5171B"
      fillRule="evenodd"
    />
  </svg>
);

const emptyProjectForm = {
  name: '',
  code: '',
  description: '',
  location: '',
  client: '',
  status: 'Active',
  startDate: '',
  employerPfLimit: null,
  esicThreshold: null,
};

const projectFormFromProject = (proj) => ({
  name: proj?.name ?? '',
  code: proj?.code || '',
  description: proj?.description || '',
  location: proj?.location || '',
  client: proj?.client || '',
  status: proj?.status || 'Active',
  startDate: proj?.startDate || '',
  employerPfLimit: getExplicitLimit(proj?.employerPfLimit),
  esicThreshold: getExplicitLimit(proj?.esicThreshold),
});

const buildProjectPayload = (form) => {
  const pf = getExplicitLimit(form?.employerPfLimit);
  const esic = getExplicitLimit(form?.esicThreshold);
  const capsOn = pf != null || esic != null;
  return {
    ...form,
    employerPfLimit: capsOn ? (pf ?? 0) : null,
    esicThreshold: capsOn ? (esic ?? 0) : null,
  };
};

const fmtCap = (value) => {
  const n = getExplicitLimit(value);
  return n == null ? '—' : `₹${n.toLocaleString('en-IN')}`;
};

const parseCapInput = (raw) => {
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
};

export default function HierarchyPage() {
  const { hasAccess } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  
  const [projectOpen, setProjectOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [projectForm, setProjectForm] = useState({ ...emptyProjectForm });
  const [companyOpen, setCompanyOpen] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({ name: '', code: '', address: '', phone: '', email: '', status: 'Active' });
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [empsData, projsData, companiesData] = await Promise.all([
          employeeApi.getAll().catch(() => []),
          projectApi.getAll().catch(() => []),
          companyApi.getAll().catch(() => []),
        ]);
        setEmployees(empsData);
        setProjects(projsData);
        setCompanies(Array.isArray(companiesData) ? companiesData : []);
        
        // Auto-expand Abd Admin root and top-level managers
        const topLevel = empsData.filter(e => !e.reportingManager || e.reportingManager === 'none');
        const initExpanded = { 'abd-admin': true };
        topLevel.forEach(e => { initExpanded[e.empId] = true; });
        setExpanded(initExpanded);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const topLevelEmployees = useMemo(() => {
    return employees.filter(e => !e.reportingManager || e.reportingManager === 'none' || e.reportingManager === '');
  }, [employees]);

  const getDirectReports = (empId) => {
    return employees.filter(e => e.reportingManager === empId);
  };

  const toggleExpand = (empId) => {
    setExpanded(prev => ({ ...prev, [empId]: !prev[empId] }));
  };

  const expandAll = () => {
    const all = { 'abd-admin': true };
    employees.forEach(e => { all[e.empId] = true; });
    setExpanded(all);
  };

  const collapseAll = () => {
    setExpanded({});
  };

  const handleSaveProject = async () => {
    if (!projectForm.name) {
      toast.error('Project name is required');
      return;
    }
    try {
      if (editProject) {
        const updated = await projectApi.update(editProject.projectId, buildProjectPayload(projectForm));
        setProjects(prev => prev.map(p => p.projectId === editProject.projectId ? { ...p, ...updated } : p));
        toast.success('Project updated');
      } else {
        const newProj = await projectApi.create(buildProjectPayload(projectForm));
        setProjects(prev => [...prev, newProj]);
        toast.success('Project created');
      }
      setProjectOpen(false);
      setEditProject(null);
      setProjectForm({ ...emptyProjectForm });
    } catch (err) {
      toast.error(err.message || 'Failed to save project');
    }
  };

  const handleDeleteProject = async (id) => {
    try {
      await projectApi.remove(id);
      setProjects(prev => prev.filter(p => p.projectId !== id));
      toast.success('Project deleted');
    } catch (err) {
      toast.error(err.message || 'Failed to delete project');
    }
  };

  const handleDeleteCompany = async (id) => {
    try {
      await companyApi.remove(id);
      setCompanies(prev => prev.filter(c => c.companyId !== id));
      toast.success('Company deleted');
    } catch (err) {
      toast.error(err?.message ?? 'Failed to delete company');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    const { type, id } = deleteTarget;
    setDeleteTarget(null);
    if (type === 'project') await handleDeleteProject(id);
    if (type === 'company') await handleDeleteCompany(id);
  };

  const handleSaveCompany = async () => {
    if (!companyForm?.name?.trim()) {
      toast.error('Company name is required');
      return;
    }
    try {
      if (editCompany?.companyId) {
        const updated = await companyApi.update(editCompany.companyId, companyForm);
        setCompanies((prev) =>
          prev.map((c) => (c.companyId === editCompany.companyId ? { ...c, ...updated } : c))
        );
        toast.success('Company updated');
      } else {
        const created = await companyApi.create(companyForm);
        setCompanies((prev) => [...prev, created]);
        toast.success('Company created');
      }
      setCompanyOpen(false);
      setEditCompany(null);
      setCompanyForm({ name: '', code: '', address: '', phone: '', email: '', status: 'Active' });
    } catch (err) {
      toast.error(err?.message ?? 'Failed to save company');
    }
  };

  const getProjectEmployeeCount = (projectId) => {
    return employees.filter(e => (e.projects || []).includes(projectId)).length;
  };

  // Render an org node with proper indentation
  const renderOrgNode = (emp, level) => {
    const empId = emp.empId || emp.id;
    const directReports = getDirectReports(empId);
    const hasReports = directReports.length > 0;
    const isExpanded = expanded[empId];

    return (
      <div key={empId} data-testid={`org-node-${empId}`}>
        <div 
          className={`flex items-center gap-2 p-3 rounded-lg border mb-1 transition-all cursor-pointer ${level === 0 ? 'bg-[#0047AB]/10 border-[#0047AB]/30' : 'bg-background border-border hover:border-[#0047AB]/30'}`}
          onClick={() => hasReports && toggleExpand(empId)}
          style={{ marginLeft: `${level * 24}px` }}
        >
          {hasReports ? (
            <span className="w-5 h-5 flex items-center justify-center">
              {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </span>
          ) : (
            <span className="w-5" />
          )}
          
          {emp.picture ? (
            <img src={emp.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#0047AB]/10 flex items-center justify-center text-xs font-bold text-[#0047AB]">
              {emp.firstName?.charAt(0)}{emp.lastName?.charAt(0)}
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{emp.firstName} {emp.lastName}</p>
            <p className="text-xs text-muted-foreground truncate">{emp.designation}</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">{emp.department}</Badge>
            {hasReports && <Badge className="bg-[#0047AB]/10 text-[#0047AB] border-[#0047AB]/30 text-[10px]">{directReports.length}</Badge>}
          </div>
        </div>
        
        {hasReports && isExpanded && level < 5 && directReports.map(report => renderOrgNode(report, level + 1))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="hierarchy-page" className="space-y-3 animate-fadeIn">
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-[Manrope]">Organization Hierarchy</h1>
        <p className="text-muted-foreground mt-1">View org chart and manage projects and companies</p>
      </div>

      <Tabs defaultValue="orgchart" className="space-y-4">
        <TabsList>
          <TabsTrigger value="orgchart" data-testid="tab-orgchart">
            <GitBranch className="w-4 h-4 mr-1" /> Org Chart
          </TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-projects">
            <FolderOpen className="w-4 h-4 mr-1" /> Projects
          </TabsTrigger>
          <TabsTrigger value="companies" data-testid="tab-companies">
            <Building2 className="w-4 h-4 mr-1" /> Companies
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orgchart" className="space-y-4">
          <Card className="border border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base font-[Manrope] flex items-center gap-2">
                <Users className="w-4 h-4 text-[#0047AB]" />
                Organization Structure
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={expandAll} data-testid="expand-all-btn">Expand All</Button>
                <Button size="sm" variant="outline" onClick={collapseAll} data-testid="collapse-all-btn">Collapse All</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              <div data-testid="org-node-abd-admin">
                <div
                  className="flex items-center gap-2 p-3 rounded-lg border mb-1 transition-all cursor-pointer bg-[#0047AB]/10 border-[#0047AB]/30"
                  onClick={() => topLevelEmployees.length > 0 && toggleExpand('abd-admin')}
                >
                  {topLevelEmployees.length > 0 ? (
                    <span className="w-5 h-5 flex items-center justify-center">
                      {expanded['abd-admin'] ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </span>
                  ) : (
                    <span className="w-5" />
                  )}
                  <div className="w-8 h-8 rounded-full bg-white border border-border flex items-center justify-center overflow-hidden shrink-0">
                    <AbdLogo className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">ABD Admin</p>
                    {/* <p className="text-xs text-muted-foreground truncate">Organization</p> */}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">ABD</Badge>
                    {topLevelEmployees.length > 0 && (
                      <Badge className="bg-[#0047AB]/10 text-[#0047AB] border-[#0047AB]/30 text-[10px]">
                        {topLevelEmployees.length}
                      </Badge>
                    )}
                  </div>
                </div>
                {topLevelEmployees.length > 0 && expanded['abd-admin'] && topLevelEmployees.map((emp) => renderOrgNode(emp, 1))}
                {topLevelEmployees.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No organization structure found</p>
                    <p className="text-xs mt-1">Set reporting managers in employee profiles to build the hierarchy</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border border-border shadow-sm">
              <CardContent className="p-4 text-center">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Employees</p>
                <p className="text-2xl font-bold font-mono mt-1 text-[#0047AB]">{employees.length}</p>
              </CardContent>
            </Card>
            <Card className="border border-border shadow-sm">
              <CardContent className="p-4 text-center">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Top-Level</p>
                <p className="text-2xl font-bold font-mono mt-1 text-[#10b981]">{topLevelEmployees.length}</p>
              </CardContent>
            </Card>
            <Card className="border border-border shadow-sm">
              <CardContent className="p-4 text-center">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Departments</p>
                <p className="text-2xl font-bold font-mono mt-1 text-[#f59e0b]">{[...new Set(employees.map(e => e.department))].length}</p>
              </CardContent>
            </Card>
            <Card className="border border-border shadow-sm">
              <CardContent className="p-4 text-center">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Projects</p>
                <p className="text-2xl font-bold font-mono mt-1 text-[#6366f1]">{projects.length}</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="projects">
          <Card className="border border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base font-[Manrope]">Projects</CardTitle>
              {hasAccess('Admin') && (
                <Button
                  size="sm"
                  onClick={() => { setEditProject(null); setProjectForm({ ...emptyProjectForm }); setProjectOpen(true); }}
                  className="bg-[#0047AB] hover:bg-[#003d96] text-white"
                  data-testid="add-project-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Project
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Code</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Location</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Client</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">PF Limit</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">ESIC Threshold</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Employees</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map(proj => (
                    <TableRow key={proj.projectId} data-testid={`project-row-${proj.projectId}`}>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">{proj.name}</p>
                          <p className="text-xs text-muted-foreground">{proj.description}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{proj.code}</TableCell>
                      <TableCell className="text-sm">{proj.location}</TableCell>
                      <TableCell className="text-sm">{proj.client}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtCap(proj.employerPfLimit)}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtCap(proj.esicThreshold)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">{getProjectEmployeeCount(proj.projectId)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={proj.status === 'Active' ? 'bg-green-100 text-green-700 border-green-200' : proj.status === 'Completed' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}>
                          {proj.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditProject(proj); setProjectForm(projectFormFromProject(proj)); setProjectOpen(true); }} data-testid={`edit-project-${proj.projectId}`}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          {hasAccess('Admin') && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => setDeleteTarget({ type: 'project', id: proj.projectId, name: proj.name })} data-testid={`delete-project-${proj.projectId}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {projects.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                        <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No projects created yet</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="companies">
          <Card className="border border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base font-[Manrope]">Companies</CardTitle>
              {hasAccess('Admin') && (
                <Button
                  size="sm"
                  onClick={() => {
                    setEditCompany(null);
                    setCompanyForm({ name: '', code: '', address: '', phone: '', email: '', status: 'Active' });
                    setCompanyOpen(true);
                  }}
                  className="bg-[#0047AB] hover:bg-[#003d96] text-white"
                  data-testid="add-company-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Company
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Address</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Phone</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Email</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.map((company) => (
                    <TableRow key={company.companyId} data-testid={`company-row-${company.companyId}`}>
                      <TableCell className="text-sm font-medium">{company.name}</TableCell>
                      <TableCell className="text-sm">{company.address}</TableCell>
                      <TableCell className="text-sm">{company.phone}</TableCell>
                      <TableCell className="text-sm">{company.email}</TableCell>
                      <TableCell>
                        <Badge className={company.status === 'Active' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}>
                          {company.status || 'Active'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={() => {
                              setEditCompany(company);
                              setCompanyForm({
                                name: company?.name ?? '',
                                code: company?.code ?? '',
                                address: company?.address ?? '',
                                phone: company?.phone ?? '',
                                email: company?.email ?? '',
                                status: company?.status ?? 'Active',
                              });
                              setCompanyOpen(true);
                            }}
                            data-testid={`edit-company-${company.companyId}`}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          {hasAccess('Admin') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                              onClick={() => setDeleteTarget({ type: 'company', id: company.companyId, name: company.name })}
                              data-testid={`delete-company-${company.companyId}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {companies.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                        <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No companies created yet</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Project Dialog */}
      <Dialog open={projectOpen} onOpenChange={setProjectOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">{editProject ? 'Edit Project' : 'Add Project'}</DialogTitle>
            <DialogDescription>Fill in the project details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Project Name *</Label>
                <Input value={projectForm.name} onChange={e => setProjectForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., NexHR Core Platform" data-testid="project-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Code</Label>
                <Input value={projectForm.code} onChange={e => setProjectForm(p => ({ ...p, code: e.target.value }))} placeholder="e.g., NXC" data-testid="project-code-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label>
              <Input value={projectForm.description} onChange={e => setProjectForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Location</Label>
                <Input value={projectForm.location} onChange={e => setProjectForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g., Bangalore" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Client</Label>
                <Input value={projectForm.client} onChange={e => setProjectForm(p => ({ ...p, client: e.target.value }))} placeholder="e.g., Internal" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Status</Label>
                <Select value={projectForm.status} onValueChange={v => setProjectForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="On Hold">On Hold</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Start Date</Label>
                <Input type="date" value={projectForm.startDate} onChange={e => setProjectForm(p => ({ ...p, startDate: e.target.value }))} />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Statutory Caps</p>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {projectForm.employerPfLimit != null || projectForm.esicThreshold != null ? 'On' : 'Off'}
                  </span>
                  <Switch
                    checked={projectForm.employerPfLimit != null || projectForm.esicThreshold != null}
                    onCheckedChange={(on) =>
                      setProjectForm((p) => ({
                        ...p,
                        employerPfLimit: on ? (p.employerPfLimit ?? 0) : null,
                        esicThreshold: on ? (p.esicThreshold ?? 0) : null,
                      }))
                    }
                    aria-label="Enable statutory caps"
                    data-testid="project-caps-toggle"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Employer PF Limit</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-mono">₹</span>
                    <Input
                      type="number"
                      min="0"
                      disabled={projectForm.employerPfLimit == null && projectForm.esicThreshold == null}
                      className="pl-6 font-mono disabled:opacity-50"
                      value={projectForm.employerPfLimit ?? 0}
                      onChange={(e) => setProjectForm((p) => ({ ...p, employerPfLimit: parseCapInput(e.target.value) }))}
                      placeholder="0"
                      data-testid="project-pf-limit-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">ESIC Threshold</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-mono">₹</span>
                    <Input
                      type="number"
                      min="0"
                      disabled={projectForm.employerPfLimit == null && projectForm.esicThreshold == null}
                      className="pl-6 font-mono disabled:opacity-50"
                      value={projectForm.esicThreshold ?? 0}
                      onChange={(e) => setProjectForm((p) => ({ ...p, esicThreshold: parseCapInput(e.target.value) }))}
                      placeholder="0"
                      data-testid="project-esic-threshold-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Turn statutory caps off to use system defaults (PF ₹15,000 / ESIC ₹21,000). When on, entered amounts are used, including 0.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProjectOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveProject} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="save-project-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company Dialog */}
      <Dialog open={companyOpen} onOpenChange={setCompanyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">{editCompany ? 'Edit Company' : 'Add Company'}</DialogTitle>
            <DialogDescription>Fill in the company details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Company Name *</Label>
                <Input value={companyForm.name} onChange={e => setCompanyForm(c => ({ ...c, name: e.target.value }))} placeholder="e.g., ABD Construction" data-testid="company-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Code</Label>
                <Input value={companyForm.code} onChange={e => setCompanyForm(c => ({ ...c, code: e.target.value }))} placeholder="e.g., ABD" data-testid="company-code-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Address</Label>
              <Input value={companyForm.address} onChange={e => setCompanyForm(c => ({ ...c, address: e.target.value }))} placeholder="e.g., New Delhi" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Phone</Label>
                <Input value={companyForm.phone} onChange={e => setCompanyForm(c => ({ ...c, phone: e.target.value }))} placeholder="e.g., 9999999999" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                <Input value={companyForm.email} onChange={e => setCompanyForm(c => ({ ...c, email: e.target.value }))} placeholder="e.g., info@abd.com" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Status</Label>
              <Select value={companyForm.status} onValueChange={(v) => setCompanyForm(c => ({ ...c, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompanyOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCompany} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="save-company-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={deleteTarget?.type === 'company' ? 'Delete Company?' : 'Delete Project?'}
        description={`Are you sure you want to delete "${deleteTarget?.name || 'this item'}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        data-testid="hierarchy-delete-confirm"
      />
    </div>
  );
}
